"use client";

import { useMemo } from "react";
import { stegaClean } from "@sanity/client/stega";
import TopBanner from "@/components/global/banner/TopBanner";
import { useCampaignLive } from "@/hooks/useCampaignLive";
import { isCampaignWindowLive } from "@/lib/scheduledCampaign";
import {
  CAMPAIGN_PREVIEW_PARAM,
  type IPageTopBanner,
  resolveTopBanner,
} from "@/lib/resolveBanners";
import type { IBannerCampaign } from "@/sanity/globals/BannerCampaign";

/**
 * Picks the page's top banner and switches it at the campaign boundaries in the visitor's
 * browser: the HTML is cached, so a server-side clock check would be frozen into it.
 */
export default function TopBannerSlot({
  page,
  campaign,
  language,
  renderedAt,
}: {
  page: IPageTopBanner;
  campaign: IBannerCampaign | null;
  language?: string;
  /** Server clock when the page was rendered: the first render must match the server markup. */
  renderedAt: number;
}) {
  const { isEnabled, startDate, endDate } = campaign ?? {};
  // draft mode adds invisible stega characters to strings, which break date parsing
  const schedule = useMemo(
    () => stegaClean({ isEnabled, startDate, endDate }),
    [isEnabled, startDate, endDate],
  );
  const live = useCampaignLive(
    schedule,
    CAMPAIGN_PREVIEW_PARAM,
    isCampaignWindowLive(schedule, renderedAt),
  );

  const banner = resolveTopBanner(page, campaign, live);
  if (!banner) return null;

  // keyed so a swap replays the entrance animation and restarts the countdown
  return (
    <TopBanner key={banner._id} data={banner} language={language} renderedAt={renderedAt} />
  );
}
